import { useEffect, useMemo, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { MapPin, RotateCcw } from "lucide-react";
import { scopeLabels } from "../data/provinces";
import type { ProvinceStat, Scope } from "../types";

type ChinaProvinceProperties = {
  adcode: number | string;
  name: string;
  center?: [number, number];
  centroid?: [number, number];
};

type ChinaProvinceFeature = Feature<Geometry, ChinaProvinceProperties>;
type ChinaProvinceCollection = FeatureCollection<Geometry, ChinaProvinceProperties>;

type AtlasMapProps = {
  stats: ProvinceStat[];
  selectedProvince: string | null;
  activeScope: Scope;
  onSelectProvince: (provinceId: string | null) => void;
};

const MAP_WIDTH = 820;
const MAP_HEIGHT = 640;
const MAX_RANKED_LABELS = 18;
const ALWAYS_LABEL_CODES = new Set([
  "15", // 内蒙古
  "23", // 黑龙江
  "45", // 广西
  "46", // 海南
  "50", // 重庆
  "52", // 贵州
  "53", // 云南
  "54", // 西藏
  "62", // 甘肃
  "63", // 青海
  "65" // 新疆
]);
const LABEL_OFFSETS_BY_CODE: Record<string, [number, number]> = {
  "11": [0, -8], // 北京
  "32": [22, -10], // 江苏
  "34": [-8, 10] // 安徽
};

function getMapFill(count: number, maxCount: number, isSelected: boolean) {
  if (isSelected) return "oklch(0.58 0.19 25)";
  if (count === 0) return "oklch(0.91 0.006 80)";

  const ratio = Math.max(0.12, count / Math.max(maxCount, 1));
  const lightness = 0.93 - ratio * 0.33;
  const chroma = 0.035 + ratio * 0.095;
  return `oklch(${lightness.toFixed(3)} ${chroma.toFixed(3)} 245)`;
}

function getAdminCode(feature: ChinaProvinceFeature) {
  return String(feature.properties.adcode).slice(0, 2);
}

function offsetLabelPoint(point: [number, number] | null, code?: string) {
  if (!point || !code) return point;
  const offset = LABEL_OFFSETS_BY_CODE[code];
  if (!offset) return point;
  return [point[0] + offset[0], point[1] + offset[1]] as [number, number];
}

function normalizeGeometryWinding(geometry: Geometry): Geometry {
  const next = JSON.parse(JSON.stringify(geometry)) as Geometry;

  if (next.type === "Polygon") {
    next.coordinates = next.coordinates.map((ring) => [...ring].reverse());
  }

  if (next.type === "MultiPolygon") {
    next.coordinates = next.coordinates.map((polygon) =>
      polygon.map((ring) => [...ring].reverse())
    );
  }

  return next;
}

function normalizeMapData(data: ChinaProvinceCollection): ChinaProvinceCollection {
  return {
    ...data,
    features: data.features.map((feature) => ({
      ...feature,
      geometry: normalizeGeometryWinding(feature.geometry)
    }))
  };
}

export function AtlasMap({
  stats,
  selectedProvince,
  activeScope,
  onSelectProvince
}: AtlasMapProps) {
  const [geoData, setGeoData] = useState<ChinaProvinceCollection | null>(null);
  const [mapLoadState, setMapLoadState] = useState<"loading" | "ready" | "error">("loading");
  const maxCount = Math.max(...stats.map((stat) => stat.count), 1);
  const selected = stats.find((stat) => stat.id === selectedProvince);
  const statsByCode = useMemo(() => new Map(stats.map((stat) => [stat.code, stat])), [stats]);

  useEffect(() => {
    let cancelled = false;

    async function loadMap() {
      try {
        const response = await fetch("/data/china-provinces.json");
        if (!response.ok) throw new Error("Failed to load China province geometry");
        const data = (await response.json()) as ChinaProvinceCollection;
        if (!cancelled) {
          setGeoData(normalizeMapData(data));
          setMapLoadState("ready");
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) setMapLoadState("error");
      }
    }

    loadMap();
    return () => {
      cancelled = true;
    };
  }, []);

  const mapModel = useMemo(() => {
    if (!geoData) return null;

    const visibleFeatures = geoData.features.filter((feature) =>
      /^\d{6}$/.test(String(feature.properties.adcode))
    );
    const visibleCollection: ChinaProvinceCollection = {
      ...geoData,
      features: visibleFeatures
    };
    const projection = geoMercator().fitExtent(
      [
        [18, 18],
        [MAP_WIDTH - 18, MAP_HEIGHT - 18]
      ],
      visibleCollection
    );
    const path = geoPath(projection);

    const provinces = visibleFeatures.map((feature) => {
      const stat = statsByCode.get(getAdminCode(feature));
      const provinceId = stat?.id ?? null;
      const isSelected = provinceId === selectedProvince;
      const center = feature.properties.centroid ?? feature.properties.center;
      const projectedLabelPoint = center ? projection(center) : path.centroid(feature);
      const labelPoint = offsetLabelPoint(projectedLabelPoint, stat?.code);

      return {
        feature,
        pathData: path(feature),
        stat,
        provinceId,
        isSelected,
        labelPoint
      };
    });

    const labelCandidates = provinces.filter(
      (item) => item.stat && item.labelPoint && item.stat.count > 0
    );
    const rankedLabels = [...labelCandidates]
      .sort((a, b) => (b.stat?.count ?? 0) - (a.stat?.count ?? 0))
      .slice(0, MAX_RANKED_LABELS);
    const labelsById = new Map<string, (typeof labelCandidates)[number]>();

    for (const item of rankedLabels) {
      if (item.stat) labelsById.set(item.stat.id, item);
    }

    for (const item of labelCandidates) {
      if (!item.stat) continue;
      if (ALWAYS_LABEL_CODES.has(item.stat.code) || item.isSelected) {
        labelsById.set(item.stat.id, item);
      }
    }

    const labels = [...labelsById.values()].sort((a, b) => {
      if (a.isSelected) return 1;
      if (b.isSelected) return -1;
      return (a.labelPoint?.[1] ?? 0) - (b.labelPoint?.[1] ?? 0);
    });

    return { provinces, labels };
  }, [geoData, selectedProvince, statsByCode]);

  const totalStats = {
    count: stats.reduce((sum, item) => sum + item.count, 0),
    undergraduate: stats.reduce((sum, item) => sum + item.undergraduate, 0),
    vocational: stats.reduce((sum, item) => sum + item.vocational, 0),
    privateCount: stats.reduce((sum, item) => sum + item.privateCount, 0)
  };

  return (
    <section className="map-shell" aria-labelledby="map-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ATLAS VIEW</p>
          <h2 id="map-title">省份分布图</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={() => onSelectProvince(null)}
          title="重置省份"
          aria-label="重置省份"
        >
          <RotateCcw size={16} />
          <span>重置</span>
        </button>
      </div>

      <div className="map-layout">
        <div className="china-map-frame">
          {mapLoadState !== "ready" || !mapModel ? (
            <div className="map-loading" role="status">
              {mapLoadState === "error" ? "地图边界加载失败" : "正在加载省级地图"}
            </div>
          ) : (
            <svg
              className="china-map-svg"
              viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
              role="img"
              aria-label={`${scopeLabels[activeScope]}中国省级分布图`}
            >
              <g>
                {mapModel.provinces.map((item) => {
                  const title = item.stat
                    ? `${item.stat.name}: ${item.stat.count} 所`
                    : item.feature.properties.name || "南海诸岛";
                  return (
                    <path
                      key={String(item.feature.properties.adcode)}
                      className={`map-province${item.isSelected ? " is-selected" : ""}${
                        item.stat ? "" : " is-reference"
                      }`}
                      d={item.pathData ?? undefined}
                      fill={getMapFill(item.stat?.count ?? 0, maxCount, item.isSelected)}
                      role={item.stat ? "button" : "img"}
                      tabIndex={item.stat ? 0 : -1}
                      aria-label={title}
                      aria-pressed={item.stat ? item.isSelected : undefined}
                      onClick={() => {
                        if (!item.provinceId) return;
                        onSelectProvince(item.isSelected ? null : item.provinceId);
                      }}
                      onKeyDown={(event) => {
                        if (!item.provinceId) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectProvince(item.isSelected ? null : item.provinceId);
                        }
                      }}
                    >
                      <title>{title}</title>
                    </path>
                  );
                })}
              </g>
              <g className="map-label-layer" aria-hidden="true">
                {mapModel.labels.map((item) => (
                  <text
                    key={`${item.stat?.id}-label`}
                    x={item.labelPoint?.[0]}
                    y={item.labelPoint?.[1]}
                    className={item.isSelected ? "is-selected" : ""}
                  >
                    {item.stat?.shortName}
                    <tspan x={item.labelPoint?.[0]} dy="1.15em">
                      {item.stat?.count}
                    </tspan>
                  </text>
                ))}
              </g>
            </svg>
          )}

          <div className="map-footer">
            <div className="map-legend" aria-label="地图颜色图例">
              <span>少</span>
              <i className="legend-step legend-step-1" />
              <i className="legend-step legend-step-2" />
              <i className="legend-step legend-step-3" />
              <i className="legend-step legend-step-4" />
              <span>多</span>
            </div>
            <p>地图边界仅用于数据定位与可视化展示。</p>
          </div>
        </div>

        <aside className="province-inspector" aria-live="polite">
          <div className="inspector-kicker">
            <MapPin size={16} />
            <span>{selected ? "所选省份" : "全国概览"}</span>
          </div>
          <h3>{selected?.name ?? "所有省份"}</h3>
          <p>
            当前范围为 <strong>{scopeLabels[activeScope]}</strong>
            {selected ? "，右侧目录已同步筛选到该省份。" : "，点击任一省份查看局部高校列表。"}
          </p>
          <dl className="stat-list">
            <div>
              <dt>高校总数</dt>
              <dd>{selected?.count ?? totalStats.count}</dd>
            </div>
            <div>
              <dt>本科</dt>
              <dd>{selected?.undergraduate ?? totalStats.undergraduate}</dd>
            </div>
            <div>
              <dt>专科</dt>
              <dd>{selected?.vocational ?? totalStats.vocational}</dd>
            </div>
            <div>
              <dt>民办</dt>
              <dd>{selected?.privateCount ?? totalStats.privateCount}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </section>
  );
}
